import type {ReactElement} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import type {AssistantMessageImagePartAnimationCreationsProps, AiChatAnimation} from './assistant-message-image-part-animation-creations.types';
import {AnimationCreationStatus} from './assistant-message-image-part-animation-creations.types';
import styles from './assistant-message-image-part-animation-creations.module.scss';
import {SkeletonBrick, SkeletonBrickTheme} from '@Components/skeleton-brick';
import {Text} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {AnimationGenerationErrorCode, cancelAnimationGeneration, ensureAnimationCredits, generateAnimationForAiChatImage} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import type {AnimationGenerationError} from '@Components/ai-chat-window/lib/ai-chat-animation-generation.library';
import {AiChatTurnToVideoSource, AiChatTurnToVideoType, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {AiChatCostType, getUrlForAiChatAnimation, getUrlForAiChatAnimationThumbnail} from '@Libraries/ai-chat.library';
import {AnimationLightbox} from '@Components/ai-chat-window/components/animation-lightbox/animation-lightbox';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import TooltipTrigger from '@Components/tooltip-trigger';

export function AssistantMessageImagePartAnimationCreations({...props}: AssistantMessageImagePartAnimationCreationsProps): ReactElement | null {
  const {state, actions} = useAiChat();
  const animationCreationState = state.getAnimationCreationState(props.chatId, props.hashedId);
  const [isHovered, setIsHovered] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cardHeight, setCardHeight] = useState<number | undefined>(undefined);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const persistedAnimations: AiChatAnimation[] = useMemo(() => {
    if (!props.animations?.length) return [];
    return [...props.animations].reverse().map((metadata) => ({
      animationHashedId: metadata.animationHashedId,
      animationUrl: getUrlForAiChatAnimation(metadata.animationHashedId),
      thumbnailUrl: getUrlForAiChatAnimationThumbnail(metadata.animationHashedId),
      width: metadata.width,
      height: metadata.height,
      duration: metadata.duration,
      hasAudio: metadata.hasAudio,
      model: metadata.model,
      startTime: metadata.startTime,
      endTime: metadata.endTime,
    }));
  }, [props.animations]);

  const hasPersistedAnimations = persistedAnimations.length > 0;
  const isVisible = animationCreationState.isVisible || hasPersistedAnimations;

  if (!isVisible) {
    return null;
  }

  const stateAnimations = animationCreationState.animations ?? [];
  const effectiveAnimations =
    stateAnimations.length > 0
      ? [...stateAnimations, ...persistedAnimations.filter((p) => !stateAnimations.some((s) => s.animationHashedId === p.animationHashedId))]
      : persistedAnimations;
  const effectiveStatus = hasPersistedAnimations && !animationCreationState.isVisible ? AnimationCreationStatus.COMPLETED : animationCreationState.status;
  const thumbnailAspectRatio =
    props.imageWidth && props.imageHeight
      ? props.imageWidth / props.imageHeight
      : effectiveAnimations[0]?.width && effectiveAnimations[0]?.height
        ? effectiveAnimations[0].width / effectiveAnimations[0].height
        : 9 / 16;

  const handleCreateMore = (): void => {
    if (animationCreationState.status === AnimationCreationStatus.CREATING) {
      return;
    }

    const generateAudio = animationCreationState.generateAudio ?? false;

    void (async (): Promise<void> => {
      const hasCredits = await ensureAnimationCredits(generateAudio);
      if (!hasCredits) {
        return;
      }

      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TURNTOVIDEO, {
        [GA4EventParamName.SOURCE]: AiChatTurnToVideoSource.DESIGN,
        [GA4EventParamName.TYPE]: generateAudio ? AiChatTurnToVideoType.WITH_AUDIO : AiChatTurnToVideoType.SILENT,
      });

      const abortController = new AbortController();
      const existingAnimations = [...effectiveAnimations];

      actions.updateAnimationCreationState(props.chatId, props.hashedId, {
        isVisible: true,
        status: AnimationCreationStatus.CREATING,
        abortController,
        animations: existingAnimations,
      });

      try {
        const animation = await generateAnimationForAiChatImage(props.hashedId, {
          signal: abortController.signal,
          onPollingKeyReceived: (pollingKey: string): void => {
            actions.updateAnimationCreationState(props.chatId, props.hashedId, {pollingKey});
          },
          generateAudio,
        });
        actions.updateAnimationCreationState(props.chatId, props.hashedId, {
          status: AnimationCreationStatus.COMPLETED,
          animations: [animation, ...existingAnimations],
          abortController: undefined,
          pollingKey: undefined,
        });
        actions.refreshCredits?.();
      } catch (e) {
        if ((e as AnimationGenerationError).code === AnimationGenerationErrorCode.INSUFFICIENT_CREDITS) {
          actions.updateAnimationCreationState(props.chatId, props.hashedId, {
            status: existingAnimations.length > 0 ? AnimationCreationStatus.COMPLETED : AnimationCreationStatus.IDLE,
            animations: existingAnimations,
            abortController: undefined,
            pollingKey: undefined,
          });
          const hasPurchasedCredits = await ensureAnimationCredits(generateAudio);
          if (hasPurchasedCredits) {
            handleCreateMore();
          }
          return;
        }

        if (!abortController.signal.aborted) {
          actions.updateAnimationCreationState(props.chatId, props.hashedId, {
            status: AnimationCreationStatus.FAILED,
            errorMessage: window.i18next.t('pmwjs_animation_creation_failed'),
            abortController: undefined,
            pollingKey: undefined,
          });
        }
      }
    })();
  };

  const handleAudioOptionChange = (generateAudio: boolean): void => {
    actions.updateAnimationCreationState(props.chatId, props.hashedId, {generateAudio});
  };

  const getAudioOptionsContent = (): ReactElement => {
    return <AudioOptionsContent isAudioSelected={animationCreationState.generateAudio ?? false} onSelect={handleAudioOptionChange} />;
  };

  const handleSettingsClick = (): void => {
    if (state.isSmall) {
      openMoreOptionsModal({
        headerTitle: window.i18next.t('pmwjs_animation_settings'),
        content: <AudioOptionsModalContent initialValue={animationCreationState.generateAudio ?? false} onSelect={handleAudioOptionChange} />,
        className: styles.settingsModalContent,
        shouldCenterAlignTitle: true,
      });
      return;
    }
    if (cardRef.current) {
      setCardHeight(cardRef.current.offsetHeight);
    }
    setShowSettings(true);
  };

  const handleStopGeneration = (): void => {
    if (animationCreationState.pollingKey) {
      void cancelAnimationGeneration(animationCreationState.pollingKey);
    }

    animationCreationState.abortController?.abort();

    const hasExistingAnimations = effectiveAnimations.length > 0;

    actions.updateAnimationCreationState(props.chatId, props.hashedId, {
      isVisible: !hasExistingAnimations,
      status: hasExistingAnimations ? AnimationCreationStatus.IDLE : AnimationCreationStatus.STOPPED,
      abortController: undefined,
      pollingKey: undefined,
    });
  };

  const getSubtitle = (): string => {
    switch (effectiveStatus) {
      case AnimationCreationStatus.CREATING:
        return window.i18next.t(state.isSmall ? 'pmwjs_creating_animation_ready_soon' : 'pmwjs_creating_animation_may_take_time');
      case AnimationCreationStatus.FAILED:
        return window.i18next.t('pmwjs_animation_creation_failed');
      case AnimationCreationStatus.STOPPED:
        return window.i18next.t('pmwjs_animation_generation_stopped');
      default:
        return window.i18next.t('pmwjs_download_and_publish_your_videos');
    }
  };

  const getHeader = (): ReactElement => {
    const isFailed = effectiveStatus === AnimationCreationStatus.FAILED;
    return (
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Text className={styles.title} val={window.i18next.t('pmwjs_animation_creations')} />
          <Text className={`${styles.subtitle} ${isFailed ? styles.subtitleError : ''}`} val={getSubtitle()} />
        </div>
        <Button type={Type.SECONDARY} size={Size.XSMALL} icon={'icon-filter-1'} onClick={handleSettingsClick} customClasses={styles.settingsButton} />
      </div>
    );
  };

  const getCreateMoreOption = (): ReactElement => {
    const isCreating = effectiveStatus === AnimationCreationStatus.CREATING;
    const showRetry = effectiveStatus === AnimationCreationStatus.FAILED || effectiveStatus === AnimationCreationStatus.STOPPED;
    const icon = showRetry ? 'icon-rotate-cw' : 'icon-repeat';
    const label = showRetry ? window.i18next.t('pmwjs_retry') : window.i18next.t('pmwjs_create_more');
    const tile = (
      <ClickableDiv className={`${styles.createMoreTile} ${isCreating ? styles.createMoreTileDisabled : ''}`} onClick={handleCreateMore}>
        <Icon icon={icon} size={IconSize.SIZE_ICON_16} />
        <Text className={styles.createMoreText} val={label} />
      </ClickableDiv>
    );
    if (isCreating) {
      return (
        <TooltipTrigger
          text={window.i18next.t('pmwjs_create_more_unavailable_while_generating')}
          overlayClass={styles.createMoreTooltipWrapper}
          tooltipClass={styles.createMoreTooltip}
        >
          {tile}
        </TooltipTrigger>
      );
    }
    return tile;
  };

  const getCreatingThumbnail = (): ReactElement => {
    const handleCreatingClick = (): void => {
      if (state.isSmall) {
        setCurrentAnimationIndex(0);
        setIsLightboxOpen(true);
      } else {
        handleStopGeneration();
      }
    };

    return (
      <ClickableDiv
        className={styles.animationThumbnailContainer}
        onMouseEnter={(): void => setIsHovered(true)}
        onMouseLeave={(): void => setIsHovered(false)}
        onClick={handleCreatingClick}
      >
        <SkeletonBrick className={styles.animationThumbnail} theme={SkeletonBrickTheme.AI_ROTATING_GRADIENT} />
        {isHovered ? (
          <div className={styles.stopOverlay}>
            <div className={styles.stopButtonContainer}>
              <CircularProgressLoader color={LOADER_COLOR.DARK} size={LOADER_SIZE.DEFAULT} classes={styles.stopLoader} />
              <div className={styles.stopIconWrapper}>
                <Icon icon={'icon-square-rounded'} size={IconSize.SIZE_ICON_16} className={styles.stopIcon} />
              </div>
            </div>
          </div>
        ) : null}
      </ClickableDiv>
    );
  };

  const getSlot2 = (): ReactElement => {
    if (effectiveStatus === AnimationCreationStatus.CREATING) {
      return getCreatingThumbnail();
    }

    if ((effectiveStatus === AnimationCreationStatus.COMPLETED || effectiveStatus === AnimationCreationStatus.FAILED) && effectiveAnimations.length > 0) {
      const animation = effectiveAnimations[0];
      return (
        <ClickableDiv
          className={styles.animationThumbnailContainer}
          onClick={(): void => {
            setCurrentAnimationIndex(0);
            setIsLightboxOpen(true);
          }}
        >
          <img src={animation.thumbnailUrl} className={styles.animationThumbnail} alt="" />
          <div className={styles.thumbnailShadow} />
          <div className={styles.playIcon}>
            <Icon icon={'icon-play-video'} size={IconSize.SIZE_ICON_16} shape={IconShape.CIRCLE} type={IconType.SHADOW} />
          </div>
        </ClickableDiv>
      );
    }

    if (effectiveStatus === AnimationCreationStatus.FAILED || effectiveStatus === AnimationCreationStatus.STOPPED) {
      return <div className={styles.thumbnailPlaceholder} />;
    }

    return (
      <div className={styles.animationThumbnailContainer}>
        <SkeletonBrick className={styles.animationThumbnail} theme={SkeletonBrickTheme.AI_ROTATING_GRADIENT} />
      </div>
    );
  };

  const getThumbnailWithPlayIcon = (animation: AiChatAnimation, index: number): ReactElement => {
    return (
      <ClickableDiv
        className={styles.animationThumbnailContainer}
        onClick={(): void => {
          setCurrentAnimationIndex(index);
          setIsLightboxOpen(true);
        }}
      >
        <img src={animation.thumbnailUrl} className={styles.animationThumbnail} alt="" />
        <div className={styles.thumbnailShadow} />
        <div className={styles.playIcon}>
          <Icon icon={'icon-play-video'} size={IconSize.SIZE_ICON_16} shape={IconShape.CIRCLE} type={IconType.SHADOW} />
        </div>
      </ClickableDiv>
    );
  };

  const getThumbnailWithMoreCount = (animation: AiChatAnimation, index: number, moreCount: number): ReactElement => {
    if (moreCount === 1) {
      return getThumbnailWithPlayIcon(animation, index);
    }
    return (
      <ClickableDiv
        className={styles.animationThumbnailContainer}
        onClick={(): void => {
          setCurrentAnimationIndex(index);
          setIsLightboxOpen(true);
        }}
      >
        <img src={animation.thumbnailUrl} className={styles.animationThumbnail} alt="" />
        <div className={styles.thumbnailShadow} />
        <div className={styles.moreOverlay}>
          <span className={styles.moreCount}>{`+${moreCount}`}</span>
        </div>
      </ClickableDiv>
    );
  };

  const getSlot3 = (): ReactElement => {
    const isDesktop = !state.isSmall;

    if (effectiveStatus === AnimationCreationStatus.CREATING && effectiveAnimations.length > 0) {
      if (isDesktop) {
        return getThumbnailWithPlayIcon(effectiveAnimations[0], 1);
      }
      return getThumbnailWithMoreCount(effectiveAnimations[0], 1, effectiveAnimations.length);
    }

    if ((effectiveStatus === AnimationCreationStatus.COMPLETED || effectiveStatus === AnimationCreationStatus.FAILED) && effectiveAnimations.length > 1) {
      if (isDesktop) {
        return getThumbnailWithPlayIcon(effectiveAnimations[1], 1);
      }
      return getThumbnailWithMoreCount(effectiveAnimations[1], 1, effectiveAnimations.length - 1);
    }

    return <div className={styles.thumbnailPlaceholder} />;
  };

  const getSlot4 = (): ReactElement => {
    if (effectiveStatus === AnimationCreationStatus.CREATING && effectiveAnimations.length > 1) {
      return getThumbnailWithMoreCount(effectiveAnimations[1], 2, effectiveAnimations.length - 1);
    }

    if ((effectiveStatus === AnimationCreationStatus.COMPLETED || effectiveStatus === AnimationCreationStatus.FAILED) && effectiveAnimations.length > 2) {
      return getThumbnailWithMoreCount(effectiveAnimations[2], 2, effectiveAnimations.length - 2);
    }

    return <div className={styles.thumbnailPlaceholder} />;
  };

  const getContent = (): ReactElement => {
    const isDesktop = !state.isSmall;
    return (
      <div
        className={`${styles.contentRow} ${isDesktop ? styles.contentRowDesktop : ''}`}
        style={{'--thumbnail-aspect-ratio': String(thumbnailAspectRatio)} as React.CSSProperties}
      >
        {getCreateMoreOption()}
        {getSlot2()}
        {getSlot3()}
        {isDesktop ? getSlot4() : null}
      </div>
    );
  };

  const getSettingsView = (): ReactElement => {
    return (
      <div className={`${styles.tileCard} ${styles.tileCardSettings}`} style={{minHeight: cardHeight}}>
        <div className={styles.settingsHeader}>
          <ClickableDiv className={styles.settingsBackButton} onClick={(): void => setShowSettings(false)}>
            <Icon icon={'icon-arrow-left'} size={IconSize.SIZE_ICON_16} />
            <Text className={styles.settingsTitle} val={window.i18next.t('pmwjs_animation_settings')} />
          </ClickableDiv>
        </div>
        {getAudioOptionsContent()}
      </div>
    );
  };

  if (showSettings) {
    return getSettingsView();
  }

  return (
    <>
      <div ref={cardRef} className={`${styles.tileCard} ${state.isSmall ? styles.mobile : ''}`}>
        {getHeader()}
        {getContent()}
      </div>
      {isLightboxOpen && (effectiveAnimations.length > 0 || effectiveStatus === AnimationCreationStatus.CREATING) ? (
        <AnimationLightbox
          isOpen={isLightboxOpen}
          onClose={(): void => {
            setIsLightboxOpen(false);
          }}
          animations={effectiveAnimations}
          currentIndex={currentAnimationIndex}
          onSelectAnimation={setCurrentAnimationIndex}
          onAnimationTrimmed={(animationHashedId, startTime, endTime): void => {
            const updatedAnimations = effectiveAnimations.map((a) =>
              a.animationHashedId === animationHashedId ? {...a, startTime, endTime} : a
            );
            actions.updateAnimationCreationState(props.chatId, props.hashedId, {animations: updatedAnimations});
          }}
          chatId={props.chatId}
          hashedId={props.hashedId}
          isWriteActionEnabled={true}
          isCreating={effectiveStatus === AnimationCreationStatus.CREATING}
          onStopGeneration={handleStopGeneration}
        />
      ) : null}
    </>
  );
}

function AudioOptionsContent({isAudioSelected, onSelect}: {isAudioSelected: boolean; onSelect: (generateAudio: boolean) => void}): ReactElement {
  return (
    <div className={styles.audioOptions}>
      <ClickableDiv className={`${styles.audioOption} ${!isAudioSelected ? styles.audioOptionSelected : ''}`} onClick={(): void => onSelect(false)}>
        <div className={styles.audioOptionHeader}>
          <Text className={styles.audioOptionTitle} val={window.i18next.t('pmwjs_silent')} />
          <div className={styles.creditsBadge}>
            <Icon icon={'icon-credits'} size={IconSize.SIZE_ICON_12} />
            <span className={styles.creditsCount}>{AiChatCostType.ANIMATION_WITHOUT_AUDIO_COST}</span>
          </div>
        </div>
        <div className={styles.audioOptionDescription}>
          <span className={`${styles.audioDot} ${!isAudioSelected ? styles.audioDotActive : ''}`} />
          <Text className={styles.audioOptionDescriptionText} val={window.i18next.t('pmwjs_no_audio')} />
        </div>
      </ClickableDiv>
      <ClickableDiv className={`${styles.audioOption} ${isAudioSelected ? styles.audioOptionSelected : ''}`} onClick={(): void => onSelect(true)}>
        <div className={styles.audioOptionHeader}>
          <Text className={styles.audioOptionTitle} val={window.i18next.t('pmwjs_with_audio')} />
          <div className={styles.creditsBadge}>
            <Icon icon={'icon-credits'} size={IconSize.SIZE_ICON_12} />
            <span className={styles.creditsCount}>{AiChatCostType.ANIMATION_WITH_AUDIO_COST}</span>
          </div>
        </div>
        <div className={styles.audioOptionDescription}>
          <span className={`${styles.audioDot} ${isAudioSelected ? styles.audioDotActive : ''}`} />
          <Text className={styles.audioOptionDescriptionText} val={window.i18next.t('pmwjs_realistic_sound_effects')} />
        </div>
      </ClickableDiv>
    </div>
  );
}

function AudioOptionsModalContent({initialValue, onSelect}: {initialValue: boolean; onSelect: (generateAudio: boolean) => void}): ReactElement {
  const [isAudioSelected, setIsAudioSelected] = useState(initialValue);

  const handleSelect = (generateAudio: boolean): void => {
    setIsAudioSelected(generateAudio);
    onSelect(generateAudio);
  };

  return <AudioOptionsContent isAudioSelected={isAudioSelected} onSelect={handleSelect} />;
}
